import React, { useEffect, useState } from "react";
import List from "../../styledComponents/Table/List";
import PLAID_API from "../../../apis/plaid.api";
import SearchIcon from "../../../assets/images/DeathModuleNew/Search.png";
import swal from "sweetalert";
import "../../Skeletons/SkeletonStyle/SkeletonStyle.css";
import "./plaidCostConfiguration.css";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 50;

const getRows = (response) => {
  const data = response && response.data;

  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.rows)) return data.rows;
  if (data && Array.isArray(data.data)) return data.data;
  if (data && data.result && Array.isArray(data.result)) return data.result;
  if (data && data.list && Array.isArray(data.list)) return data.list;
  if (data && data.records && Array.isArray(data.records)) return data.records;
  if (data && data.items && Array.isArray(data.items)) return data.items;

  return [];
};

const getValue = (edits, record, field) => {
  if (edits[record.id] && edits[record.id][field] !== undefined) {
    return edits[record.id][field];
  }

  return record[field] !== undefined && record[field] !== null
    ? record[field]
    : "";
};

const parseCurrencyInput = (value) =>
  String(value === undefined || value === null ? "" : value)
    .replace(/[^0-9.-]/g, "")
    .replace(/(?!^)-/g, "");

const formatUsdNumber = (value) => {
  const parsedValue = Number(parseCurrencyInput(value));

  if (value === "" || value === null || value === undefined) return "";
  if (Number.isNaN(parsedValue)) return "";

  return parsedValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const trimCurrencyForEdit = (value) => {
  const parsedValue = parseCurrencyInput(value);

  if (!parsedValue || parsedValue.endsWith(".")) return parsedValue;

  return parsedValue.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "");
};

const toNumber = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  return Number(parseCurrencyInput(value));
};

const isEnabled = (value) => value === true || value === 1 || value === "true";

const PlaidCostTableSkeleton = ({ columns = 4, rows = 4 }) => (
  <div className="plaidCostSkeleton">
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div
        className="plaidCostSkeletonRow"
        key={`plaid-cost-skeleton-${rowIndex}`}
        style={{
          gridTemplateColumns:
            columns === 5
              ? "0.7fr repeat(3, 1fr) 0.7fr"
              : "0.7fr 1.4fr 1fr 0.7fr",
        }}>
        {Array.from({ length: columns }).map((__, columnIndex) => (
          <div
            className="skeleton"
            key={`plaid-cost-skeleton-${rowIndex}-${columnIndex}`}
            style={{
              width: columnIndex === 0 ? "60%" : "100%",
              height: "28px",
              marginBottom: "0",
            }}></div>
        ))}
      </div>
    ))}
  </div>
);

const PlaidCostConfiguration = () => {
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState("");
  const [focusedField, setFocusedField] = useState("");
  const [configRows, setConfigRows] = useState([]);
  const [pricingRows, setPricingRows] = useState([]);
  const [configEdits, setConfigEdits] = useState({});
  const [pricingEdits, setPricingEdits] = useState({});
  const [plaidProduct, setPlaidProduct] = useState("");

  const updateConfigEdit = (id, field, value) => {
    setConfigEdits((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value,
      },
    }));
  };

  const updatePricingEdit = (id, field, value) => {
    setPricingEdits((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value,
      },
    }));
  };

  const renderCurrencyInput = (record, field, value, onChange, hasEdit) => {
    const inputKey = `${field}-${record.id}`;
    const isFocused = focusedField === inputKey;
    const displayValue = isFocused
      ? hasEdit
        ? value
        : trimCurrencyForEdit(value)
      : formatUsdNumber(value);

    return (
      <div className="plaidCurrencyInputWrap">
        <span className="plaidCurrencySymbol">$</span>
        <input
          className="plaidCostInput plaidCurrencyInput"
          inputMode="decimal"
          value={displayValue}
          onFocus={() => setFocusedField(inputKey)}
          onBlur={() => setFocusedField("")}
          onChange={(e) => onChange(parseCurrencyInput(e.target.value))}
        />
      </div>
    );
  };

  const fetchConfig = async () => {
    const res = await PLAID_API.fetchPlaidCostConfig(
      DEFAULT_PAGE,
      DEFAULT_LIMIT
    );
    setConfigRows(getRows(res));
  };

  const fetchPricing = async (product = plaidProduct) => {
    const res = await PLAID_API.fetchPlaidCostProductPricing(
      DEFAULT_PAGE,
      DEFAULT_LIMIT,
      product
    );
    setPricingRows(getRows(res));
  };

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchConfig(), fetchPricing()]);
    } catch (error) {
      console.log(error);
      swal("Error", "Failed to load Plaid cost configuration.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const saveConfig = async (record) => {
    const edited = configEdits[record.id] || {};
    const payload = {
      id: record.id,
      monthlyBudgetCeilingUsd: toNumber(
        edited.monthlyBudgetCeilingUsd !== undefined
          ? edited.monthlyBudgetCeilingUsd
          : record.monthlyBudgetCeilingUsd
      ),
      defaultUnitPriceUsd: toNumber(
        edited.defaultUnitPriceUsd !== undefined
          ? edited.defaultUnitPriceUsd
          : record.defaultUnitPriceUsd
      ),
      balanceSpikeRatio: toNumber(
        edited.balanceSpikeRatio !== undefined
          ? edited.balanceSpikeRatio
          : record.balanceSpikeRatio
      ),
    };

    try {
      setSavingKey(`config-${record.id}`);
      const res = await PLAID_API.updatePlaidCostConfig(payload);
      if (res && res.status === 400) {
        swal("Error", res.message || "Failed to update config.", "error");
      } else {
        swal("Success", "Plaid cost config updated successfully.", "success");
        setConfigEdits((prev) => ({ ...prev, [record.id]: undefined }));
        await fetchConfig();
      }
    } catch (error) {
      console.log(error);
      swal("Error", "Failed to update config.", "error");
    } finally {
      setSavingKey("");
    }
  };

  const savePricing = async (record) => {
    const edited = pricingEdits[record.id] || {};
    const payload = {
      id: record.id,
      unitPriceUsd: toNumber(
        edited.unitPriceUsd !== undefined
          ? edited.unitPriceUsd
          : record.unitPriceUsd
      ),
    };

    if (record.enable !== undefined && record.enable !== null) {
      payload.enable = isEnabled(record.enable);
    }

    try {
      setSavingKey(`pricing-${record.id}`);
      const res = await PLAID_API.updatePlaidCostProductPricing(payload);
      if (res && res.status === 400) {
        swal("Error", res.message || "Failed to update product pricing.", "error");
      } else {
        swal("Success", "Plaid product pricing updated successfully.", "success");
        setPricingEdits((prev) => ({ ...prev, [record.id]: undefined }));
        await fetchPricing();
      }
    } catch (error) {
      console.log(error);
      swal("Error", "Failed to update product pricing.", "error");
    } finally {
      setSavingKey("");
    }
  };

  const configCols = [
    {
      title: "ID",
      dataIndex: "id",
      render: (record) => (record ? record : "N/A"),
    },
    {
      title: "Monthly Budget Ceiling (USD)",
      render: (record) => (
        renderCurrencyInput(
          record,
          "monthlyBudgetCeilingUsd",
          getValue(configEdits, record, "monthlyBudgetCeilingUsd"),
          (value) =>
            updateConfigEdit(
              record.id,
              "monthlyBudgetCeilingUsd",
              value
            ),
          configEdits[record.id] &&
            configEdits[record.id].monthlyBudgetCeilingUsd !== undefined
        )
      ),
    },
    {
      title: "Default Unit Price (USD)",
      render: (record) => (
        renderCurrencyInput(
          record,
          "defaultUnitPriceUsd",
          getValue(configEdits, record, "defaultUnitPriceUsd"),
          (value) => updateConfigEdit(record.id, "defaultUnitPriceUsd", value),
          configEdits[record.id] &&
            configEdits[record.id].defaultUnitPriceUsd !== undefined
        )
      ),
    },
    {
      title: "Balance Spike Ratio",
      render: (record) => (
        <input
          className="plaidCostInput"
          type="number"
          value={getValue(configEdits, record, "balanceSpikeRatio")}
          onChange={(e) =>
            updateConfigEdit(record.id, "balanceSpikeRatio", e.target.value)
          }
        />
      ),
    },
    {
      title: "Action",
      render: (record) => (
        <button
          className="plaidCostSaveButton"
          disabled={savingKey === `config-${record.id}`}
          onClick={() => saveConfig(record)}>
          {savingKey === `config-${record.id}` ? "Saving..." : "Save"}
        </button>
      ),
    },
  ];

  const pricingCols = [
    {
      title: "ID",
      dataIndex: "id",
      render: (record) => (record ? record : "N/A"),
    },
    {
      title: "Plaid Product",
      dataIndex: "plaidProduct",
      render: (record) => (record ? record : "N/A"),
    },
    {
      title: "Unit Price (USD)",
      render: (record) => (
        renderCurrencyInput(
          record,
          "unitPriceUsd",
          getValue(pricingEdits, record, "unitPriceUsd"),
          (value) => updatePricingEdit(record.id, "unitPriceUsd", value),
          pricingEdits[record.id] &&
            pricingEdits[record.id].unitPriceUsd !== undefined
        )
      ),
    },
    {
      title: "Action",
      render: (record) => (
        <button
          className="plaidCostSaveButton"
          disabled={savingKey === `pricing-${record.id}`}
          onClick={() => savePricing(record)}>
          {savingKey === `pricing-${record.id}` ? "Saving..." : "Save"}
        </button>
      ),
    },
  ];

  const handleProductSearch = async () => {
    try {
      setLoading(true);
      await fetchPricing(plaidProduct);
    } catch (error) {
      console.log(error);
      swal("Error", "Failed to load product pricing.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mainRole">
      <div className="rolesTable">
        <div className="RolesHeader">
          <div className="RoleTitle">Plaid Cost Config</div>
        </div>
        {loading ? (
          <PlaidCostTableSkeleton columns={5} />
        ) : (
          <List cols={configCols} rows={configRows} scroll={900} />
        )}
      </div>

      <div className="rolesTable plaidCostTable">
        <div className="RolesHeader">
          <div className="RoleTitle">Plaid Product Pricing</div>

          <div className="plaidCostFilter">
            <img width="20px" height="20px" src={SearchIcon} alt="search" />
            <input
              className="searchInput"
              type="text"
              name="plaidProduct"
              placeholder="Plaid Product"
              value={plaidProduct}
              onChange={(e) => setPlaidProduct(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleProductSearch();
              }}
            />
            <button className="plaidCostSaveButton" onClick={handleProductSearch}>
              Filter
            </button>
          </div>
        </div>
        {loading ? (
          <PlaidCostTableSkeleton columns={4} />
        ) : (
          <List cols={pricingCols} rows={pricingRows} scroll={900} />
        )}
      </div>
    </div>
  );
};

export default PlaidCostConfiguration;
